import { Activity, Moon, Coffee, UtensilsCrossed, Cigarette, Wine, Heart } from 'lucide-react';

interface LifestyleStepProps {
  activityLevel: string;
  sleepQuality: number;
  sleepHours: number | null;
  stressLevel: string;
  diet: string;
  caffeineCups: number | null;
  alcoholDrinks: number | null;
  smoking: string;
  updateFormData: (field: string, value: string | number | null) => void;
}

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary', description: 'Little to no exercise' },
  { value: 'lightly_active', label: 'Lightly Active', description: '1-2 days per week' },
  { value: 'moderately_active', label: 'Moderately Active', description: '3-5 days per week' },
  { value: 'very_active', label: 'Very Active', description: '6-7 days per week' },
  { value: 'extremely_active', label: 'Extremely Active', description: 'Physical job + daily exercise' },
];

const STRESS_LEVELS = [
  'Minimal stress',
  'Low stress',
  'Moderate stress',
  'High stress',
  'Very high stress'
];

const SLEEP_QUALITY_LABELS = [
  'Very Poor',
  'Poor',
  'Fair',
  'Good',
  'Excellent'
];

export default function LifestyleStep({ 
  activityLevel, 
  sleepQuality, 
  sleepHours,
  stressLevel,
  diet,
  caffeineCups,
  alcoholDrinks,
  smoking,
  updateFormData 
}: LifestyleStepProps) {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <p className="text-sm font-medium uppercase tracking-wider" style={{ color: '#2FFFC7' }}>
          Step 4 of 7
        </p>
        <h2 className="text-3xl font-light tracking-tight" style={{ color: '#E6E9F0' }}>
          Lifestyle & Habits
        </h2>
        <p className="text-lg" style={{ color: '#C1C9D2' }}>
          Help us understand your daily routines and habits
        </p>
      </div>

      {/* Exercise/Activity Level */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Physical Activity Level
          </h3>
        </div>
        <div className="space-y-3">
          {ACTIVITY_LEVELS.map((level) => (
            <button
              key={level.value}
              type="button"
              onClick={() => updateFormData('activityLevel', level.value)}
              className="w-full p-4 rounded-lg text-left transition-all"
              style={{
                background: activityLevel === level.value 
                  ? 'rgba(47, 255, 199, 0.1)' 
                  : 'rgba(255, 255, 255, 0.02)',
                border: activityLevel === level.value 
                  ? '2px solid #2FFFC7' 
                  : '1px solid rgba(255, 255, 255, 0.05)',
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium" style={{ color: '#E6E9F0' }}>
                    {level.label}
                  </p>
                  <p className="text-sm" style={{ color: '#9DA9B8' }}>
                    {level.description}
                  </p>
                </div>
                {activityLevel === level.value && (
                  <div 
                    className="h-5 w-5 rounded-full flex items-center justify-center"
                    style={{ background: '#2FFFC7' }}
                  >
                    <div className="h-2 w-2 rounded-full bg-black" />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Sleep Quality */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Moon className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Sleep Quality
          </h3>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm" style={{ color: '#C1C9D2' }}>
              Rate your typical sleep quality:
            </span>
            <span 
              className="text-sm font-medium px-3 py-1 rounded"
              style={{
                color: '#2FFFC7',
                background: 'rgba(47, 255, 199, 0.1)',
              }}
            >
              {SLEEP_QUALITY_LABELS[sleepQuality]}
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="4"
            value={sleepQuality}
            onChange={(e) => updateFormData('sleepQuality', parseInt(e.target.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-[#2FFFC7]"
            style={{
              background: `linear-gradient(to right, #EF4444 0%, #F59E0B 25%, #F59E0B 50%, #10B981 75%, #2FFFC7 100%)`,
            }}
          />
          <div className="flex justify-between text-xs" style={{ color: '#9DA9B8' }}>
            <span>Very Poor</span>
            <span>Excellent</span>
          </div>
        </div>

        {/* Sleep Hours */}
        <div className="mt-4">
          <label className="block text-sm font-medium mb-2" style={{ color: '#E6E9F0' }}>
            Average hours of sleep per night
          </label>
          <input
            type="number"
            value={sleepHours ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                updateFormData('sleepHours', null);
              } else {
                const num = parseFloat(val);
                if (!isNaN(num) && num >= 0 && num <= 24) {
                  updateFormData('sleepHours', num);
                }
              }
            }}
            placeholder="7-9 hours recommended"
            step="0.5"
            className="w-full px-4 py-3 rounded-lg text-sm"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(47, 255, 199, 0.2)',
              color: '#000000',
            }}
          />
        </div>
      </div>

      {/* Stress Level */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Heart className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Stress Level
          </h3>
        </div>
        <select
          value={stressLevel}
          onChange={(e) => updateFormData('stressLevel', e.target.value)}
          className="w-full px-4 py-3 rounded-lg text-sm appearance-none cursor-pointer"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(47, 255, 199, 0.2)',
            color: stressLevel ? '#000000' : '#6B7280',
          }}
        >
          <option value="" style={{ background: '#1a2332', color: '#C1C9D2' }}>
            Select your typical stress level...
          </option>
          {STRESS_LEVELS.map((level, index) => (
            <option 
              key={index} 
              value={level}
              style={{ background: '#1a2332', color: '#E6E9F0' }}
            >
              {level}
            </option>
          ))}
        </select>
      </div>

      {/* Diet */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <UtensilsCrossed className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Diet & Nutrition
          </h3>
        </div>
        <select
          value={diet}
          onChange={(e) => updateFormData('diet', e.target.value)}
          className="w-full px-4 py-3 rounded-lg text-sm appearance-none cursor-pointer"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(47, 255, 199, 0.2)',
            color: diet ? '#000000' : '#6B7280',
          }}
        >
          <option value="" style={{ background: '#1a2332', color: '#C1C9D2' }}>Select your typical diet...</option>
          <option value="standard" style={{ background: '#1a2332', color: '#E6E9F0' }}>Standard American Diet</option>
          <option value="mediterranean" style={{ background: '#1a2332', color: '#E6E9F0' }}>Mediterranean</option>
          <option value="low_carb" style={{ background: '#1a2332', color: '#E6E9F0' }}>Low Carb / Keto</option>
          <option value="plant_based" style={{ background: '#1a2332', color: '#E6E9F0' }}>Plant-Based / Vegetarian</option>
          <option value="paleo" style={{ background: '#1a2332', color: '#E6E9F0' }}>Paleo</option>
          <option value="other" style={{ background: '#1a2332', color: '#E6E9F0' }}>Other</option>
        </select>
      </div>

      {/* Substance Use */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
          Substance Use
        </h3>

        {/* Caffeine */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Coffee className="h-4 w-4" style={{ color: '#9DA9B8' }} />
            <label className="text-sm font-medium" style={{ color: '#E6E9F0' }}>
              Caffeine (cups of coffee/tea/day)
            </label>
          </div>
          <input
            type="number"
            value={caffeineCups ?? ''}
            onChange={(e) => updateFormData('caffeineCups', e.target.value ? parseInt(e.target.value) : null)}
            placeholder="Number of cups"
            min="0"
            max="20"
            className="w-full px-4 py-3 rounded-lg text-sm"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(47, 255, 199, 0.2)',
              color: '#000000',
            }}
          />
        </div>

        {/* Alcohol */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Wine className="h-4 w-4" style={{ color: '#9DA9B8' }} />
            <label className="text-sm font-medium" style={{ color: '#E6E9F0' }}>
              Alcohol (drinks per week)
            </label>
          </div>
          <input
            type="number"
            value={alcoholDrinks ?? ''}
            onChange={(e) => updateFormData('alcoholDrinks', e.target.value ? parseInt(e.target.value) : null)}
            placeholder="Number of drinks"
            min="0"
            max="50"
            className="w-full px-4 py-3 rounded-lg text-sm"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(47, 255, 199, 0.2)',
              color: '#000000',
            }}
          />
        </div>

        {/* Smoking */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Cigarette className="h-4 w-4" style={{ color: '#9DA9B8' }} />
            <label className="text-sm font-medium" style={{ color: '#E6E9F0' }}>
              Do you smoke or use tobacco products?
            </label>
          </div>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => updateFormData('smoking', 'no')}
              className="flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all"
              style={{
                background: smoking === 'no' ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                border: smoking === 'no' ? '2px solid #2FFFC7' : '1px solid rgba(255, 255, 255, 0.05)',
                color: '#E6E9F0',
              }}
            >
              No
            </button>
            <button
              type="button"
              onClick={() => updateFormData('smoking', 'yes')}
              className="flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all"
              style={{
                background: smoking === 'yes' ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                border: smoking === 'yes' ? '2px solid #2FFFC7' : '1px solid rgba(255, 255, 255, 0.05)',
                color: '#E6E9F0',
              }}
            >
              Yes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
